import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import {
  ArrowRight, Heart, Eye, ShieldCheck, ClipboardCheck, Info,
  CalendarCheck, Users, PenLine, BookOpen,
} from 'lucide-react';
import { PublicNavbar } from '../components/brand/PublicNavbar';
import { Footer } from '../components/brand/Footer';
import { Seo } from '../components/seo/Seo';
import { organizationSchema, breadcrumbSchema, faqSchema } from '../lib/seoSchemas';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/* ───────────────────────────────────────────────────────────────────────
   For Families — public page (Phase 7 Prompt 3): what happens after
   discharge, how the status view works, and a plain-language intro to
   advance care planning with the signing-vs-effective distinction.
   Reference content only — the standard disclaimer applies throughout.
   ─────────────────────────────────────────────────────────────────── */

const AFTER_DISCHARGE = [
  {
    icon: CalendarCheck,
    title: 'A real plan, not a promise',
    body: 'When your person is sent home, their referral becomes an accepted placement with a committed first-visit window — a specific clinician, a specific time.',
  },
  {
    icon: Eye,
    title: 'A quiet window in',
    body: 'If your person shares access with you, you see the same status their care team sees: who accepted, when the first visit lands, read-only, no phone tag.',
  },
  {
    icon: ShieldCheck,
    title: 'Nothing shared without consent',
    body: 'Access is granted by the patient, scoped to exactly what they choose, and every view is logged. No surprise exposure of their health information.',
  },
];

const FAQS = [
  {
    q: 'What is an advance directive?',
    a: 'An umbrella term for legal documents — like a living will and a healthcare proxy (medical power of attorney) — that record who can speak for a person and what care they would or wouldn\u2019t want if they can no longer speak for themselves.',
  },
  {
    q: 'What\u2019s the difference between a living will and a healthcare proxy?',
    a: 'A living will records treatment preferences in writing; a healthcare proxy names a person (an agent) to make medical decisions. Many states combine both into a single advance-directive form.',
  },
  {
    q: 'When does an advance directive take effect?',
    a: 'Signing makes a document valid, but a living will or healthcare proxy generally only guides care once the person can no longer make or communicate decisions. POLST/MOLST-style forms are different — they are medical orders that clinicians follow as soon as a clinician signs them.',
  },
  {
    q: 'Do we need a lawyer to complete an advance directive?',
    a: 'Generally no. Every state publishes free statutory forms through its health department or attorney general. Most states require adult witnesses, a notary, or both — the form\u2019s instructions spell out exactly what your state expects.',
  },
  {
    q: 'What is a POLST or MOLST form?',
    a: 'A portable medical order for people with serious illness or frailty, completed with a clinician and signed by one. Its name varies by state (POLST, MOLST, POST, MOST), and unlike a living will it is an active medical order the moment it is signed.',
  },
  {
    q: 'What should we do with the documents once they\u2019re signed?',
    a: 'Get copies on file with the care team, the hospital, and the named healthcare agent — a signed document sitting in a drawer helps no one. CaraLoom\u2019s care teams track which documents are on file and when to revisit them.',
  },
];

export default function ForFamiliesPage() {
  const [state, setState] = useState('');
  const [ref, setRef] = useState(null);

  useEffect(() => {
    let live = true;
    axios.get(`${API}/acp/reference/advance-directives${state ? `?state=${state}` : ''}`)
      .then((res) => { if (live) setRef(res.data); })
      .catch(() => { if (live) setRef(null); });
    return () => { live = false; };
  }, [state]);

  return (
    <div className="min-h-screen bg-cn-bg text-cn-text" data-testid="for-families-page">
      <Seo
        title="For Families — What Happens After Hospital Discharge | CaraLoom"
        description="A plain-language guide for families: what happens after a hospital discharge, how CaraLoom's family status view works, and the basics of advance care planning — including the difference between signing a directive and when it takes effect."
        path="/for-families"
        keywords={['what happens after hospital discharge', 'family caregiver status view', 'advance care planning basics', 'advance directive basics', 'healthcare proxy explained', 'living will vs POLST', 'goals of care conversation']}
        schema={[organizationSchema(), breadcrumbSchema([{ name: 'Home', path: '/' }, { name: 'For families', path: '/for-families' }]), faqSchema(FAQS)]}
      />
      <PublicNavbar />

      {/* Hero */}
      <section className="relative overflow-hidden border-b border-cn-border">
        <div className="cn-container pt-16 md:pt-24 pb-16 md:pb-20">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-cn-bone-200/80 dark:bg-white/5 border border-cn-border mb-6">
              <Heart className="w-3.5 h-3.5 text-cn-primary" aria-hidden="true" />
              <span className="text-xs font-semibold tracking-wide uppercase text-cn-text">
                For families & caregivers
              </span>
            </div>
            <h1 className="cn-display text-[2.6rem] sm:text-5xl lg:text-6xl leading-[1.04] tracking-tight"
                data-testid="families-hero-headline">
              The days after discharge shouldn&apos;t be a{' '}
              <span className="italic font-medium text-cn-primary dark:text-cn-accent">mystery.</span>
            </h1>
            <p className="mt-6 text-lg md:text-xl text-cn-text-muted leading-relaxed max-w-2xl">
              Here&apos;s what actually happens when your person is sent home with care —
              who&apos;s coming, when the first visit lands, and how the decisions that
              matter get documented.
            </p>
            <div className="mt-9 flex flex-wrap gap-3">
              <Link to="/auth?role=patient" className="cn-btn cn-btn-primary text-base font-bold"
                data-testid="families-cta-find-care">
                Find care for my family <ArrowRight className="w-4 h-4" />
              </Link>
              <a href="#acp-basics" className="cn-btn bg-white dark:bg-cn-ink-800 text-cn-text border border-cn-border hover:bg-cn-bone-100 dark:hover:bg-cn-ink-700 text-base"
                data-testid="families-cta-acp">
                Advance care planning basics <BookOpen className="w-4 h-4" />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* What happens after discharge */}
      <section className="py-20 md:py-24 bg-cn-bone-100 dark:bg-cn-ink-900 border-b border-cn-border" data-testid="families-after-discharge">
        <div className="cn-container">
          <div className="max-w-2xl mb-12">
            <div className="cn-eyebrow mb-3">After the discharge order</div>
            <h2 className="cn-display text-4xl md:text-5xl leading-[1.05] tracking-tight">
              What you can expect, step by step.
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {AFTER_DISCHARGE.map((t) => (
              <div key={t.title} className="cn-card p-8 bg-white dark:bg-cn-ink-800"
                data-testid={`families-step-${t.title.toLowerCase().replace(/[^a-z]+/g, '-').slice(0, 24)}`}>
                <span className="w-12 h-12 rounded-2xl bg-cn-sage-100 flex items-center justify-center">
                  <t.icon className="w-6 h-6 text-cn-primary" />
                </span>
                <h3 className="mt-5 font-display font-semibold text-xl text-cn-text">{t.title}</h3>
                <p className="mt-2.5 text-cn-text-muted leading-relaxed">{t.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ACP basics */}
      <section id="acp-basics" className="py-20 md:py-28 scroll-mt-20" data-testid="families-acp-basics">
        <div className="cn-container">
          <div className="grid lg:grid-cols-[1.05fr_.95fr] gap-12 lg:gap-16 items-start">
            <div>
              <div className="cn-eyebrow mb-3">Advance care planning, in plain language</div>
              <h2 className="cn-display text-4xl md:text-5xl leading-[1.05] tracking-tight">
                Decisions, documented — <span className="text-cn-text-muted">before the hard day.</span>
              </h2>
              <p className="mt-5 text-cn-text-muted leading-relaxed max-w-lg">
                Advance care planning is a set of conversations and documents that record
                what your person wants — who speaks for them, what care they&apos;d choose,
                and what they&apos;d decline. CaraLoom helps families and care teams
                <em> track</em> these decisions; it never makes them for you.
              </p>

              <div className="mt-8 rounded-2xl border border-cn-border bg-cn-bone-100 dark:bg-cn-ink-900 p-6"
                data-testid="families-signing-vs-effective">
                <div className="flex items-center gap-2 font-semibold text-cn-text mb-3">
                  <PenLine className="w-4 h-4 text-cn-primary" />
                  Signed isn&apos;t the same as in effect
                </div>
                <ul className="space-y-3 text-sm text-cn-text-muted leading-relaxed">
                  <li>
                    <span className="font-medium text-cn-text">Signing</span> is what makes a document
                    valid — most states require witnesses, a notary, or both, and the rules differ
                    by state and by document.
                  </li>
                  <li>
                    <span className="font-medium text-cn-text">Taking effect</span> is different: a
                    living will or healthcare proxy usually only guides care when your person can&apos;t
                    speak for themselves — while POLST/MOLST forms are medical orders that clinicians
                    follow as soon as a clinician signs them.
                  </li>
                  <li>
                    A signed document sitting in a drawer helps no one — the last step is always
                    getting it <span className="font-medium text-cn-text">on file with the care team</span>.
                  </li>
                </ul>
              </div>

              <div className="mt-5 flex items-start gap-2 text-xs text-cn-text-muted" data-testid="families-acp-disclaimer">
                <Info className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                Reference content only — CaraLoom does not provide medical or legal advice.
                Requirements vary by state; always verify with your state&apos;s official forms
                and, where needed, a qualified professional.
              </div>
            </div>

            {/* State reference */}
            <div className="rounded-3xl border border-cn-border bg-white dark:bg-cn-ink-800 p-8" data-testid="families-state-reference">
              <div className="flex items-center gap-2 mb-1">
                <ClipboardCheck className="w-5 h-5 text-cn-primary" />
                <h3 className="font-display font-semibold text-xl text-cn-text">Check your state</h3>
              </div>
              <p className="text-xs text-cn-text-muted mb-5">
                Common directive documents and how signing works where you live —
                all 50 states + DC.
              </p>
              <select
                value={state}
                onChange={(e) => setState(e.target.value)}
                className="w-full text-sm border border-cn-border rounded-xl px-3 py-2.5 bg-white dark:bg-cn-ink-900 mb-5"
                data-testid="families-state-select"
              >
                <option value="">General guidance (all states)</option>
                {(ref?.states || []).map((s) => (
                  <option key={s.code} value={s.code}>{s.name}</option>
                ))}
              </select>
              {ref && (
                <div className="space-y-4 text-sm" data-testid="families-state-content">
                  <div>
                    <div className="text-[0.7rem] uppercase tracking-wide font-semibold text-cn-text-muted mb-1.5">
                      Commonly recognized documents
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      {(ref.reference?.recognized_documents || []).map((d) => (
                        <span key={d} className="text-xs px-2.5 py-1 rounded-full bg-cn-sage-100 text-cn-primary">{d}</span>
                      ))}
                    </div>
                  </div>
                  <div>
                    <div className="text-[0.7rem] uppercase tracking-wide font-semibold text-cn-text-muted mb-1">Signing</div>
                    <p className="text-cn-text-muted leading-relaxed">{ref.reference?.signing_note}</p>
                  </div>
                  <div>
                    <div className="text-[0.7rem] uppercase tracking-wide font-semibold text-cn-text-muted mb-1">When it takes effect</div>
                    <p className="text-cn-text-muted leading-relaxed">{ref.reference?.effective_note}</p>
                  </div>
                  <div className="pt-3 border-t border-cn-border text-xs text-cn-text-muted">
                    {ref.reference?.official_source_hint}
                  </div>
                  {ref.reference?.state && (
                    <div className="pt-3">
                      <Link
                        to={`/for-families/advance-directives/${ref.reference.state}`}
                        className="inline-flex items-center gap-1.5 text-sm font-semibold text-cn-primary hover:underline"
                        data-testid="families-state-full-reference-link"
                      >
                        Read the full {ref.reference.state_name || ref.reference.state} reference
                        <ArrowRight className="w-3.5 h-3.5" />
                      </Link>
                    </div>
                  )}
                  <div className="text-[0.7rem] text-cn-text-muted/80 italic">
                    {ref.decision_support_notice}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* FAQ — visible content matching the FAQPage JSON-LD */}
      <section className="py-20 md:py-24 border-t border-cn-border" data-testid="families-faq">
        <div className="cn-container">
          <div className="max-w-2xl mb-10">
            <div className="cn-eyebrow mb-3">Common questions</div>
            <h2 className="cn-display text-4xl md:text-5xl leading-[1.05] tracking-tight">
              Advance-directive basics, answered.
            </h2>
          </div>
          <div className="grid md:grid-cols-2 gap-x-10 gap-y-8 max-w-5xl">
            {FAQS.map((f, i) => (
              <div key={f.q} data-testid={`families-faq-item-${i}`}>
                <h3 className="font-display font-semibold text-lg text-cn-text">{f.q}</h3>
                <p className="mt-2 text-sm text-cn-text-muted leading-relaxed">{f.a}</p>
              </div>
            ))}
          </div>
          <div className="mt-8 flex items-start gap-2 text-xs text-cn-text-muted max-w-3xl">
            <Info className="w-3.5 h-3.5 shrink-0 mt-0.5" />
            Reference content only — CaraLoom does not provide medical or legal advice.
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 md:py-24 bg-cn-bone-100 dark:bg-cn-ink-900 border-t border-cn-border" data-testid="families-final-cta">
        <div className="cn-container text-center">
          <Users className="w-8 h-8 text-cn-primary mx-auto" />
          <h2 className="cn-display text-4xl md:text-5xl mt-5 leading-[1.04] tracking-tight max-w-2xl mx-auto">
            Be in the loop, the calm way.
          </h2>
          <p className="mt-4 text-lg text-cn-text-muted max-w-xl mx-auto">
            Your person grants access; you see the status. That&apos;s the whole deal.
          </p>
          <div className="mt-8">
            <Link to="/auth?role=patient" className="cn-btn cn-btn-primary text-base font-bold"
              data-testid="families-final-cta-btn">
              Find care for my family <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
